'use client';

import { PropsWithChildren, useEffect, useLayoutEffect } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { useCurrentSociety } from '@/hooks/publicSearch/useCurrentSociety';
import usePublicSearchLimit from '@/components/publicSearch/HeroSection/usePublicSearchLimit';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import SocietyNotFoundPage from '@/components/publicSearch/SocietyNotFound/SocietyNotFoundPage';
import SocietyLoadingScreen from '@/components/publicSearch/SocietyNotFound/SocietyLoadingScreen';

export default function SocietyLayout({ children }: PropsWithChildren) {
  const society = useCurrentSociety();
  const setSociety = usePublicSearchContext((context) => context.setSociety);
  const storedSocietyCode = usePublicSearchContext(
    (context) => context.society.societyCode ?? '',
  );
  const storedSocietyName = usePublicSearchContext(
    (context) => context.society.name?.trim() ?? '',
  );
  const pathname = usePathname() ?? '';
  const orgNameParam = useParams<{ orgName?: string | string[] }>().orgName;
  const urlSocietyCode =
    (Array.isArray(orgNameParam) ? orgNameParam[0] : orgNameParam)?.trim() ??
    '';

  usePublicSearchLimit();

  const isResolvingSociety =
    Boolean(urlSocietyCode) && storedSocietyCode !== urlSocietyCode;

  // Prefer the live society lookup; the persisted store can briefly have a
  // matching societyCode (gate passes, /{code} → /animals redirect runs)
  // before name is written back via setSociety in useEffect.
  const societyPageTitleName =
    (!society.loading && !society.notFound && society.name?.trim()) ||
    storedSocietyName;

  const applySocietyDocumentTitle = () => {
    if (
      society.notFound ||
      isResolvingSociety ||
      !societyPageTitleName ||
      !urlSocietyCode
    ) {
      return;
    }
    document.title = `${societyPageTitleName} - Database Search`;
  };

  // Set the tab title for every route under /{societyCode}/ (bare /{code}
  // redirect, animals, members, etc.). pathname is a dep so we re-apply after
  // client navigations that reset document.title to root metadata.
  useLayoutEffect(() => {
    applySocietyDocumentTitle();
  }, [
    society.notFound,
    isResolvingSociety,
    societyPageTitleName,
    urlSocietyCode,
    pathname,
  ]);

  useEffect(() => {
    applySocietyDocumentTitle();
  }, [
    society.notFound,
    isResolvingSociety,
    societyPageTitleName,
    urlSocietyCode,
    pathname,
  ]);

  // The public-search store is persisted to sessionStorage, so on any
  // navigation into a society URL the store can momentarily hold the
  // *previous* society's data (logo, tabs, settings, name → document.title).
  // Navigation.tsx is an ancestor of this layout and subscribes to that
  // store, so without this guard it would render the old society's branded
  // header for one frame before any of our post-render effects reconcile.
  //
  // Reset synchronously (useLayoutEffect blocks the browser paint, so the
  // re-render it triggers is what actually paints) whenever the URL's
  // societyCode doesn't match the stored one — including when the stored
  // one is empty (first visit / just-cleared notFound state).
  useLayoutEffect(() => {
    // During browser back/forward, `useParams()` can briefly yield an empty or
    // array-shaped segment. Clearing the store here leaves `storedSocietyCode`
    // out of sync with the real URL and traps the layout behind the loading
    // gate (useCurrentSociety may skip with empty code and never repopulate).
    if (!urlSocietyCode) return;
    const current = usePublicSearchContext.getState().society;
    const storedCode = current.societyCode ?? '';
    if (urlSocietyCode !== storedCode) {
      setSociety({ loading: true, orgId: '' });
    }
  }, [urlSocietyCode, setSociety]);

  // Commit the resolved society (or the not-found flag) to the store once
  // the lookup finishes. The useLayoutEffect above has already cleared any
  // stale data, so this is purely about writing the *new* society's state.
  useEffect(() => {
    if (society.loading) return;

    if (society.notFound) {
      setSociety({ loading: false, orgId: '', notFound: true });
      return;
    }

    if (!society.orgId) return;

    setSociety(society);
  }, [society, setSociety]);

  if (society.notFound) {
    // Render inline rather than calling Next's `notFound()` — from a client
    // component that boundary doesn't reliably resolve to the segment-scoped
    // not-found.tsx and falls back to the root default 404 UI.
    return <SocietyNotFoundPage />;
  }

  // Gate children on the store catching up to the URL. Children read
  // `society` straight from the store and have their own Apollo queries keyed
  // off `orgId`; rendering them with the cleared / previous society would
  // flash stale content. While we're syncing, show the loading screen (video
  // backdrop + spinner) which sets a neutral "Loading…" document title.
  if (isResolvingSociety) {
    return <SocietyLoadingScreen />;
  }

  return children;
}
